import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InventoryFilters as FilterType } from "../../services/adminInventory";

interface InventoryFiltersProps {
  filters: Omit<FilterType, "userId">;
  onChange: (filters: Omit<FilterType, "userId">) => void;
  loading?: boolean;
}

const validationSchema = Yup.object({
  minPrice: Yup.number().min(0, "Minimum price must be positive"),
  maxPrice: Yup.number().min(0, "Maximum price must be positive"),
});

const InventoryFilters: React.FC<InventoryFiltersProps> = ({
  filters,
  onChange,
  loading = false,
}) => {
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  

  const formik = useFormik({
    initialValues: {
      q: filters.q || "",
      category: filters.category || "",
      condition: filters.condition || "",
      manufacturer: filters.manufacturer || "",
      country: filters.country || "",
      minPrice: filters.minPrice || "",
      maxPrice: filters.maxPrice || "",
      isActive: filters.isActive !== undefined ? filters.isActive.toString() : "",
    },
    validationSchema,
    onSubmit: (values) => {
      const processedFilters: Omit<FilterType, "userId"> = {};

      if (values.q) processedFilters.q = values.q;
      if (values.category) processedFilters.category = values.category;
      if (values.condition) processedFilters.condition = values.condition;
      if (values.manufacturer) processedFilters.manufacturer = values.manufacturer;
      if (values.country) processedFilters.country = values.country;
      if (values.minPrice) processedFilters.minPrice = Number(values.minPrice);
      if (values.maxPrice) processedFilters.maxPrice = Number(values.maxPrice);
      if (values.isActive !== "") {
        processedFilters.isActive = values.isActive === "true";
      }

      onChange(processedFilters);
    },
  });

  // Debounced search for q field
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      if (formik.values.q !== filters.q) {
        formik.handleSubmit();
      }
    }, 300);

    setDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.q, filters.q]);

  const handleReset = () => {
    formik.resetForm({
      values: {
        q: "",
        category: "",
        condition: "",
        manufacturer: "",
        country: "",
        minPrice: "",
        maxPrice: "",
        isActive: "",
      },
    });
    onChange({});
  };

  const categories = [
    "Drones/UAV",
    "Motors & Props",
    "Cameras & Gimbals",
    "Batteries",
    "GCS – Ground Control Systems",
    "Storage",
    "Components",
    "Software",
    "C-UAS",
    "Services",
    "Other",
  ];

  const conditions = [
    "NEW Retail",
    "New Bulk",
    "Refurbished",
    "Used",
    "AS-IS",
  ];

  return (
    <Card className="mb-4">
      <Card.Header>
        <h6 className="mb-0">Filters</h6>
      </Card.Header>
      <Card.Body>
        <form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Search */}
            <Col md={3}>
              <label htmlFor="q" className="form-label">
                Search
              </label>
              <input
                type="text"
                id="q"
                className="form-control"
                placeholder="Search items..."
                {...formik.getFieldProps("q")}
              />
            </Col>

            {/* Category */}
            <Col md={3}>
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select id="category" className="form-select" {...formik.getFieldProps("category")}>
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </Col>

            {/* Condition */}
            <Col md={3}>
              <label htmlFor="condition" className="form-label">
                Condition
              </label>
              <select id="condition" className="form-select" {...formik.getFieldProps("condition")}>
                <option value="">All Conditions</option>
                {conditions.map((cond) => (
                  <option key={cond} value={cond}>
                    {cond}
                  </option>
                ))}
              </select>
            </Col>

            {/* Status */}
            <Col md={3}>
              <label htmlFor="isActive" className="form-label">
                Status
              </label>
              <select id="isActive" className="form-select" {...formik.getFieldProps("isActive")}>
                <option value="">All Items</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </Col>
          </Row>

          <Row className="mt-3">
            {/* Manufacturer */}
            <Col md={3}>
              <label htmlFor="manufacturer" className="form-label">
                Manufacturer
              </label>
              <input
                type="text"
                id="manufacturer"
                className="form-control"
                placeholder="e.g., DJI, Parrot"
                {...formik.getFieldProps("manufacturer")}
              />
            </Col>

            {/* Country */}
            <Col md={3}>
              <label htmlFor="country" className="form-label">
                Country
              </label>
              <input
                type="text"
                id="country"
                className="form-control"
                placeholder="e.g., US, UK"
                {...formik.getFieldProps("country")}
              />
            </Col>

            {/* Min Price */}
            <Col md={3}>
              <label htmlFor="minPrice" className="form-label">
                Min Price
              </label>
              <input
                type="number"
                id="minPrice"
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
                {...formik.getFieldProps("minPrice")}
              />
              {formik.touched.minPrice && formik.errors.minPrice && (
                <div className="text-danger small mt-1">{formik.errors.minPrice}</div>
              )}
            </Col>

            {/* Max Price */}
            <Col md={3}>
              <label htmlFor="maxPrice" className="form-label">
                Max Price
              </label>
              <input
                type="number"
                id="maxPrice"
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
                {...formik.getFieldProps("maxPrice")}
              />
              {formik.touched.maxPrice && formik.errors.maxPrice && (
                <div className="text-danger small mt-1">{formik.errors.maxPrice}</div>
              )}
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <div className="d-flex gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={loading}>
                  Apply Filters
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </Card.Body>
    </Card>
  );
};

export default InventoryFilters;
